'use client';
import { Download, ExternalLink, Eye, FileText, Paperclip, TextAlignStart } from 'lucide-react';
import { useParams } from 'next/navigation';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { RichTextDisplay } from '../ui/rich-text-display';
/**
 * AgendaItem component for displaying a legislative item in an agenda view
 * Shows item number, title, summary, recommended action, and attachments
 * Includes an external link button to view details in a new tab
 */
export function AgendaItem(_a) {
    var className = _a.className, _b = _a.headingLevel, headingLevel = _b === void 0 ? 4 : _b, item = _a.item, itemNumber = _a.itemNumber, onAttachmentClick = _a.onAttachmentClick;
    var Heading = "h".concat(headingLevel);
    var params = useParams();
    var attachments = item.attachments, fullTitle = item.fullTitle, legislativeStatus = item.legislativeStatus, recommendedActionText = item.recommendedActionText, shortTitle = item.shortTitle;
    var hasMetadata = !!recommendedActionText || attachments.length > 0;
    var displayTitle = shortTitle || fullTitle;
    // Non-public items are title-only: render unlinked, no status badge (GOV-1476).
    var isNonPublic = item.isPublic === false;
    var handleViewDetails = function (item) {
        var url = "/portal/".concat(params.code, "/topics/").concat(item.id);
        window.open(url, '_blank', 'noopener,noreferrer');
    };
    return (<div className={cn('hover:bg-muted/50 bg-card space-y-3 rounded-lg border p-3 transition-colors', className)}>
      {/* Item Header */}
      <div className={cn('flex items-center justify-between gap-3', !isNonPublic &&
            'focus-visible:ring-ring/70 cursor-pointer rounded-md outline-none focus-visible:ring-2')} {...(!isNonPublic && {
        onClick: function (e) {
            e.stopPropagation();
            handleViewDetails(item);
        },
        role: 'button',
        onKeyDown: function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleViewDetails(item);
            }
        },
        tabIndex: 0,
    })}>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 text-sm leading-snug font-medium">{itemNumber}</span>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Heading className="text-foreground text-sm leading-snug font-medium">
                {displayTitle}
              </Heading>
              {(legislativeStatus === null || legislativeStatus === void 0 ? void 0 : legislativeStatus.name) && (<Badge className="text-xs" variant="info-light">
                  {legislativeStatus.name}
                </Badge>)}
            </div>
          </div>
        </div>
        {!isNonPublic && (<div>
            <ExternalLink className="h-4 w-4"/>
          </div>)}
      </div>

      {/* Metadata Section */}
      {hasMetadata && (<div className="border-border space-y-3 border-t pt-3">
          {/* Recommended Action */}
          {recommendedActionText && (<div className="flex gap-2">
              <TextAlignStart className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0"/>
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                  Recommended Action
                </p>
                <div className="text-foreground text-sm">
                  <RichTextDisplay content={recommendedActionText}/>
                </div>
              </div>
            </div>)}

          {/* Attachments */}
          {attachments.length > 0 && (<div className="flex gap-2">
              <Paperclip className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0"/>
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                  Attachments ({attachments.length})
                </p>
                <div>
                  {attachments.map(function (attachment, idx) {
                    var isPdf = attachment.extension.toLowerCase() === 'pdf';
                    var handleDownload = function (e) {
                        e.stopPropagation();
                        var link = document.createElement('a');
                        link.href = attachment.signedUrl;
                        link.download = attachment.name;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    };
                    return (<div key={"".concat(attachment.name, "-").concat(idx)} className={cn('flex items-center justify-between gap-2', idx > 0 && 'border-t pt-1', idx < attachments.length - 1 && 'pb-1')}>
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <FileText className="h-4 w-4 shrink-0"/>
                          <span className="truncate text-sm">{attachment.name}</span>
                          <Badge variant="outline" className="shrink-0 text-xs">
                            {attachment.extension.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {isPdf && (<Button aria-label={"View ".concat(attachment.name)} onClick={function (e) {
                                e.stopPropagation();
                                onAttachmentClick === null || onAttachmentClick === void 0 ? void 0 : onAttachmentClick(attachment);
                            }} size="sm" type="button" variant="ghost">
                              <Eye className="h-4 w-4"/>
                            </Button>)}
                          <Button aria-label={"Download ".concat(attachment.name)} onClick={handleDownload} size="sm" type="button" variant="ghost">
                            <Download className="h-4 w-4"/>
                          </Button>
                        </div>
                      </div>);
                })}
                </div>
              </div>
            </div>)}
        </div>)}
    </div>);
}
